import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Share2,
  Plus,
  Trash2,
  Send,
  Edit3,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { socialApi } from '@/api/client';
import type {
  SocialAccount,
  SocialPost,
  SocialPlatform,
  SocialPostStatus,
} from '@/types';

// ---- Platform icons/labels ----

const PLATFORMS: { value: SocialPlatform; label: string }[] = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'x', label: 'X / Twitter' },
  { value: 'linkedin', label: 'LinkedIn' },
];

function platformLabel(platform: SocialPlatform): string {
  return PLATFORMS.find((p) => p.value === platform)?.label ?? platform;
}

// ---- Status badges ----

const postStatusColors: Record<SocialPostStatus, string> = {
  draft: 'bg-silver-muted/20 text-silver-muted',
  scheduled: 'bg-blue-500/20 text-blue-400',
  publishing: 'bg-yellow-500/20 text-yellow-400',
  published: 'bg-status-healthy/20 text-status-healthy',
  failed: 'bg-status-error/20 text-status-error',
};

function PostStatusBadge({ status }: { status: SocialPostStatus }) {
  return (
    <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium', postStatusColors[status])}>
      {status}
    </span>
  );
}

function AccountStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-status-healthy/20 text-status-healthy',
    expired: 'bg-yellow-500/20 text-yellow-400',
    revoked: 'bg-status-error/20 text-status-error',
  };
  return (
    <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium', colors[status] ?? 'bg-silver-muted/20 text-silver-muted')}>
      {status}
    </span>
  );
}

// ---- Link Account Modal ----

function LinkAccountModal({ onClose, onLink }: { onClose: () => void; onLink: (platform: SocialPlatform, name: string) => void }) {
  const [platform, setPlatform] = useState<SocialPlatform>('youtube');
  const [accountName, setAccountName] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-jeket-black-elevated border border-jeket-gold/20 rounded-xl shadow-jeket w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-jeket-silver-bright">Link Social Account</h3>
          <button onClick={onClose} className="text-jeket-silver-muted hover:text-jeket-silver-bright">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-jeket-silver-muted mb-1">Platform</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as SocialPlatform)}
              className="w-full bg-jeket-black-surface border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright focus:outline-none focus:border-jeket-gold/50"
            >
              {PLATFORMS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-jeket-silver-muted mb-1">Account Name</label>
            <input
              type="text"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="e.g. @jeket-official"
              className="w-full bg-jeket-black-surface border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright placeholder:text-jeket-silver-muted/50 focus:outline-none focus:border-jeket-gold/50"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-jeket-silver-muted hover:text-jeket-silver-bright transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => accountName.trim() && onLink(platform, accountName.trim())}
            disabled={!accountName.trim()}
            className="px-4 py-2 text-sm bg-jeket-gold text-black rounded-lg hover:bg-jeket-gold/90 transition-colors disabled:opacity-50"
          >
            Link Account
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- New Post Modal ----

function NewPostModal({
  accounts,
  onClose,
  onCreate,
}: {
  accounts: SocialAccount[];
  onClose: () => void;
  onCreate: (data: { account_id: string; title: string; body?: string; media_url?: string; scheduled_at?: string }) => void;
}) {
  const [accountId, setAccountId] = useState(accounts[0]?.id ?? '');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [scheduleMode, setScheduleMode] = useState<'now' | 'schedule'>('now');
  const [scheduledAt, setScheduledAt] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-jeket-black-elevated border border-jeket-gold/20 rounded-xl shadow-jeket w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-jeket-silver-bright">New Post</h3>
          <button onClick={onClose} className="text-jeket-silver-muted hover:text-jeket-silver-bright">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-jeket-silver-muted mb-1">Account</label>
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="w-full bg-jeket-black-surface border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright focus:outline-none focus:border-jeket-gold/50"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {platformLabel(a.platform)} — {a.account_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-jeket-silver-muted mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Post title"
              className="w-full bg-jeket-black-surface border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright placeholder:text-jeket-silver-muted/50 focus:outline-none focus:border-jeket-gold/50"
            />
          </div>

          <div>
            <label className="block text-sm text-jeket-silver-muted mb-1">Body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              placeholder="Post content..."
              className="w-full bg-jeket-black-surface border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright placeholder:text-jeket-silver-muted/50 focus:outline-none focus:border-jeket-gold/50 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm text-jeket-silver-muted mb-1">Media URL (optional)</label>
            <input
              type="text"
              value={mediaUrl}
              onChange={(e) => setMediaUrl(e.target.value)}
              placeholder="https://..."
              className="w-full bg-jeket-black-surface border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright placeholder:text-jeket-silver-muted/50 focus:outline-none focus:border-jeket-gold/50"
            />
          </div>

          <div>
            <label className="block text-sm text-jeket-silver-muted mb-2">Publish</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm text-jeket-silver-bright cursor-pointer">
                <input
                  type="radio"
                  name="scheduleMode"
                  checked={scheduleMode === 'now'}
                  onChange={() => setScheduleMode('now')}
                  className="accent-jeket-gold"
                />
                Save as draft
              </label>
              <label className="flex items-center gap-2 text-sm text-jeket-silver-bright cursor-pointer">
                <input
                  type="radio"
                  name="scheduleMode"
                  checked={scheduleMode === 'schedule'}
                  onChange={() => setScheduleMode('schedule')}
                  className="accent-jeket-gold"
                />
                Schedule
              </label>
            </div>
            {scheduleMode === 'schedule' && (
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="mt-2 w-full bg-jeket-black-surface border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright focus:outline-none focus:border-jeket-gold/50"
              />
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-jeket-silver-muted hover:text-jeket-silver-bright transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (!title.trim() || !accountId) return;
              onCreate({
                account_id: accountId,
                title: title.trim(),
                body: body || undefined,
                media_url: mediaUrl || undefined,
                scheduled_at: scheduleMode === 'schedule' && scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
              });
            }}
            disabled={!title.trim() || !accountId}
            className="px-4 py-2 text-sm bg-jeket-gold text-black rounded-lg hover:bg-jeket-gold/90 transition-colors disabled:opacity-50"
          >
            {scheduleMode === 'schedule' ? 'Schedule Post' : 'Create Draft'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Main Page ----

export default function Social() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'accounts' | 'posts'>('accounts');
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);

  // Queries
  const { data: accounts = [], isLoading: accountsLoading } = useQuery({
    queryKey: ['social-accounts'],
    queryFn: socialApi.listAccounts,
  });

  const { data: posts = [], isLoading: postsLoading } = useQuery({
    queryKey: ['social-posts'],
    queryFn: () => socialApi.listPosts(),
  });

  // Mutations
  const linkMutation = useMutation({
    mutationFn: (data: { platform: SocialPlatform; account_name: string }) => socialApi.linkAccount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-accounts'] });
      setShowLinkModal(false);
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: (id: string) => socialApi.unlinkAccount(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['social-accounts'] }),
  });

  const createPostMutation = useMutation({
    mutationFn: (data: { account_id: string; title: string; body?: string; media_url?: string; scheduled_at?: string }) =>
      socialApi.createPost(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-posts'] });
      setShowPostModal(false);
    },
  });

  const deletePostMutation = useMutation({
    mutationFn: (id: string) => socialApi.deletePost(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['social-posts'] }),
  });

  const publishMutation = useMutation({
    mutationFn: (id: string) => socialApi.publishPost(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['social-posts'] }),
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Share2 size={24} className="text-jeket-gold" />
          <h1 className="text-2xl font-bold text-jeket-silver-bright">Social Distribution</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-jeket-gold/20">
        <button
          onClick={() => setTab('accounts')}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px',
            tab === 'accounts'
              ? 'border-jeket-gold text-jeket-gold'
              : 'border-transparent text-jeket-silver-muted hover:text-jeket-silver-bright',
          )}
        >
          Accounts
        </button>
        <button
          onClick={() => setTab('posts')}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px',
            tab === 'posts'
              ? 'border-jeket-gold text-jeket-gold'
              : 'border-transparent text-jeket-silver-muted hover:text-jeket-silver-bright',
          )}
        >
          Posts
        </button>
      </div>

      {/* Accounts Tab */}
      {tab === 'accounts' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setShowLinkModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-jeket-gold text-black rounded-lg hover:bg-jeket-gold/90 transition-colors"
            >
              <Plus size={16} />
              Link Account
            </button>
          </div>

          {accountsLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-jeket-gold" />
            </div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-12 text-jeket-silver-muted">
              No linked accounts yet. Click "Link Account" to get started.
            </div>
          ) : (
            <div className="grid gap-3">
              {accounts.map((account: SocialAccount) => (
                <div
                  key={account.id}
                  className="bg-jeket-black-elevated border border-jeket-gold/10 rounded-lg p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-jeket-black-surface border border-jeket-gold/20 flex items-center justify-center">
                      <Share2 size={18} className="text-jeket-gold" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-jeket-silver-bright">
                        {account.account_name}
                      </div>
                      <div className="text-xs text-jeket-silver-muted">
                        {platformLabel(account.platform)}
                      </div>
                    </div>
                    <AccountStatusBadge status={account.status} />
                  </div>
                  <button
                    onClick={() => unlinkMutation.mutate(account.id)}
                    className="p-2 rounded-lg text-jeket-silver-muted hover:text-jeket-error hover:bg-jeket-black-surface transition-colors"
                    title="Unlink account"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Posts Tab */}
      {tab === 'posts' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setShowPostModal(true)}
              disabled={accounts.length === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-jeket-gold text-black rounded-lg hover:bg-jeket-gold/90 transition-colors disabled:opacity-50"
              title={accounts.length === 0 ? 'Link an account first' : undefined}
            >
              <Plus size={16} />
              New Post
            </button>
          </div>

          {postsLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-jeket-gold" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12 text-jeket-silver-muted">
              No posts yet. Create your first post to get started.
            </div>
          ) : (
            <div className="bg-jeket-black-elevated border border-jeket-gold/10 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-jeket-gold/10">
                    <th className="px-4 py-3 text-left text-xs font-medium text-jeket-silver-muted uppercase">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-jeket-silver-muted uppercase">Platform</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-jeket-silver-muted uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-jeket-silver-muted uppercase">Scheduled</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-jeket-silver-muted uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post: SocialPost) => (
                    <tr key={post.id} className="border-b border-jeket-gold/5 last:border-0">
                      <td className="px-4 py-3 text-sm text-jeket-silver-bright">{post.title}</td>
                      <td className="px-4 py-3 text-sm text-jeket-silver-muted">{platformLabel(post.platform)}</td>
                      <td className="px-4 py-3">
                        <PostStatusBadge status={post.status} />
                      </td>
                      <td className="px-4 py-3 text-sm text-jeket-silver-muted">
                        {post.scheduled_at
                          ? new Date(post.scheduled_at).toLocaleString()
                          : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {(post.status === 'draft' || post.status === 'scheduled') && (
                            <>
                              <button
                                onClick={() => publishMutation.mutate(post.id)}
                                className="p-1.5 rounded text-jeket-silver-muted hover:text-jeket-gold hover:bg-jeket-black-surface transition-colors"
                                title="Publish now"
                              >
                                <Send size={14} />
                              </button>
                              <button
                                onClick={() => deletePostMutation.mutate(post.id)}
                                className="p-1.5 rounded text-jeket-silver-muted hover:text-jeket-error hover:bg-jeket-black-surface transition-colors"
                                title="Delete"
                              >
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                          {post.status === 'failed' && post.error_message && (
                            <span className="text-xs text-status-error" title={post.error_message}>
                              {post.error_message.slice(0, 40)}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showLinkModal && (
        <LinkAccountModal
          onClose={() => setShowLinkModal(false)}
          onLink={(platform, name) => linkMutation.mutate({ platform, account_name: name })}
        />
      )}

      {showPostModal && (
        <NewPostModal
          accounts={accounts}
          onClose={() => setShowPostModal(false)}
          onCreate={(data) => createPostMutation.mutate(data)}
        />
      )}
    </div>
  );
}
